import { useMutation } from '@apollo/client'
import { Box, FormModal, Link, Text } from '@chaine/keychaine'
import { AppTriggerEnum } from '@chaine/types'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'

import { Toast } from '../../../_shared/components/toast'
import { validations } from '../../../_utils/validations/validations'
import { AppModalProps } from '../components/apps-data'
import { ENABLE_ORBCOMM_APP, EnableOrbcommAppInput } from '../data/mutations'

/**
 * Saves Orbcomm app's encoded api key with workspace ID and source.
 *
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 */
const OrbcommModal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, refetchApps } = props
  const { showToast } = Toast()
  const [formError, setFormError] = useState('')

  const [enableOrbcommApp, { loading }] = useMutation<
    { enableOrbcommApp: { enableAfterCreation: boolean; isCronBasedOrEventBased: AppTriggerEnum; source: string } },
    EnableOrbcommAppInput
  >(ENABLE_ORBCOMM_APP, {
    onCompleted: () => {
      showToast({
        status: 'success',
        title: `Orbcomm app is enabled successfully!`
      })
      refetchApps()
      onClose()
      setFormError('')
    },
    onError: (err) => {
      if (err.message === 'Incorrect source') {
        setFormError('Invalid source. Please try again.')
        showToast({
          status: 'error',
          title: `Invalid source. Please try again.`
        })
      } else {
        setFormError(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
        showToast({
          status: 'error',
          title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        })
      }
    }
  })

  const closeModal = () => {
    setFormError('')
    onClose()
  }

  type FieldValues = { source: string }
  const handleSaveDetails: SubmitHandler<FieldValues> = () => {
    enableOrbcommApp({
      variables: {
        appType,
        enableAfterCreation: true,
        isCronBasedOrEventBased: AppTriggerEnum.Cron,
        source: 'Orbcomm'
      }
    })
  }

  const footer = (
    <Box>
      <Text color="red.600">{formError}</Text>
      <Text>
        {`See `}
        <Link
          color="blue.600"
          href="https://handbook.chaineapp.com/product-guide/apps/orbcomm"
          target="_blank"
          rel="noopener noreferrer"
        >
          detailed steps here.
        </Link>
      </Text>
    </Box>
  )

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={closeModal}
        title={`Enable Orbcomm for Chaine`}
        subtitle={`Clicking on Enable will allow Chaine to connect with Orbcomm`}
        formSubmitHandler={handleSaveDetails}
        buttonText={'Enable'}
        initialValues={{}}
        validations={validations}
        isLoading={loading}
        footer={formError ? footer : <></>}
        modalItems={[]}
      />
    </>
  )
})

OrbcommModal.displayName = 'OrbcommModal'

export { OrbcommModal }
